/*
 * THE HOUSE OF JK — Scent Finder
 * Ported from JSX lines 1283-1379 (ScentFinder) + 1269-1282 (findScentMatch).
 *
 * Drives sections/scent-finder.liquid. Reads its catalog from
 * <script type="application/json" data-scent-catalog>, which the section builds
 * from the merchant's collection + `jk` metafields (with product-tag fallback).
 *
 * Every user-visible string is handed over by Liquid as a data-label-* attribute
 * on [data-scent-finder]. Nothing English is written here.
 *
 * Contract with the rest of the theme:
 *   - The result card's add-to-cart is a real <form data-add-to-cart> posting to
 *     the section's data-cart-add-url, so assets/theme.js intercepts it exactly
 *     as it does for every server-rendered product card.
 *   - After the card is injected, a bubbling 'jk:compare:sync' CustomEvent is
 *     dispatched on document so assets/compare.js can adopt the new
 *     [data-compare-toggle] button.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function attr(el, name) {
    if (!el) return '';
    var v = el.getAttribute(name);
    return v == null ? '' : v;
  }

  function asList(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value !== '') return [value];
    return [];
  }

  function norm(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function listHas(list, value) {
    var wanted = norm(value);
    for (var i = 0; i < list.length; i++) {
      if (norm(list[i]) === wanted) return true;
    }
    return false;
  }

  function readCatalog(root) {
    var node = root.querySelector('script[type="application/json"][data-scent-catalog]');
    if (!node) return [];
    try {
      var data = JSON.parse(node.textContent || '[]');
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  /* --------------------------------------------------------------- match */

  /*
   * Faithful port of findScentMatch(), JSX 1269-1282. The weighting is
   * reproduced verbatim:
   *
   *   gender    +3 when equal, -2 when not — and SKIPPED ENTIRELY when the
   *             shopper answered "No Preference" (falsy answers skipped too)
   *   season    +2 when the product lists that season, otherwise +0
   *   occasion  +3 when the product lists that occasion, otherwise +0
   *   family    +4 when equal, -1 when not (family is optional; when the
   *             shopper picked none, the whole clause is skipped)
   *
   * bestScore seeds at -Infinity and the comparison is a strict `>`, which has
   * two consequences the original relied on and this port keeps:
   *   1. the FIRST product holding the top score wins (catalog order breaks ties)
   *   2. a match is ALWAYS returned for a non-empty catalog — a deeply negative
   *      total still beats -Infinity, so the original fell back to its least-bad
   *      product rather than rendering an empty state.
   * Only a genuinely empty catalog returns null; that is the one case that shows
   * the no-match line.
   */
  function findScentMatch(catalog, answers) {
    var best = null;
    var bestScore = -Infinity;

    for (var i = 0; i < catalog.length; i++) {
      var scent = catalog[i];
      var score = 0;

      if (answers.gender && answers.gender !== 'No Preference') {
        score += norm(scent.gender) === norm(answers.gender) ? 3 : -2;
      }
      if (answers.season) {
        score += listHas(asList(scent.season), answers.season) ? 2 : 0;
      }
      if (answers.occasion) {
        score += listHas(asList(scent.occasion), answers.occasion) ? 3 : 0;
      }
      if (answers.family) {
        score += norm(scent.family) === norm(answers.family) ? 4 : -1;
      }

      if (score > bestScore) {
        bestScore = score;
        best = scent;
      }
    }

    return best;
  }

  /* --------------------------------------------------------------- render */

  function bottleMarkup(root) {
    var tpl = root.querySelector('template[data-scent-bottle]');
    if (tpl) return tpl.innerHTML;
    var svg = document.querySelector('.product-card__media-spin svg');
    return svg ? svg.outerHTML : '';
  }

  /*
   * Mirrors snippets/product-card.liquid element for element so the card the
   * finder injects is indistinguishable from a server-rendered one.
   */
  function cardMarkup(root, scent, labels) {
    var url = scent.url || '#';
    var title = scent.title || '';
    var available = scent.available !== false;

    var price;
    if (scent.onSale && scent.compareAtPriceFormatted) {
      price =
        '<span class="price__sale">' + esc(scent.priceFormatted) + '</span>' +
        '<span class="price__compare">' + esc(scent.compareAtPriceFormatted) + '</span>';
    } else {
      price = esc(scent.priceFormatted);
    }

    var html = '';
    html += '<div class="product-card" data-product-card data-product-handle="' + esc(scent.handle) +
            '" data-product-title="' + esc(title) + '">';

    if (scent.image) {
      // Real photo: full-bleed --real card, matching snippets/product-card.liquid.
      html += '<a class="product-card__media product-card__media--real" href="' + esc(url) +
              '" data-product-card-media aria-label="' + esc(title) + '">';
      html += '<img class="product-card__img" src="' + esc(scent.image) + '" alt="' +
              esc(scent.imageAlt || title) + '" loading="lazy">';
      if (scent.onSale) {
        html += '<span class="product-card__badge">' + esc(labels.sale) + '</span>';
      }
      html += '</a>';
    } else {
      // No photo: the floating SVG bottle placeholder.
      html += '<a class="product-card__media product-card__media--placeholder" href="' + esc(url) +
              '" data-product-card-media aria-label="' + esc(title) + '">';
      html += '<div class="product-card__media-pop"><div class="product-card__media-spin">' +
              bottleMarkup(root) + '</div></div>';
      html += '<span class="product-card__360-badge">' + esc(labels.view360) + '</span>';
      if (scent.onSale) {
        html += '<span class="product-card__badge">' + esc(labels.sale) + '</span>';
      }
      html += '</a>';
    }

    html += '<div class="product-card__quick-add">';
    html += '<form method="post" action="' + esc(labels.cartAddUrl) +
            '" data-add-to-cart style="flex:1; display:flex;">';
    html += '<input type="hidden" name="id" value="' + esc(scent.variantId) + '">';
    html += '<input type="hidden" name="quantity" value="1">';
    html += '<button type="submit" style="flex:1;"' + (available ? '' : ' disabled') + '>' +
            esc(available ? labels.add : labels.soldOut) + '</button>';
    html += '</form>';
    html += '<button type="button" data-compare-toggle data-product-handle="' + esc(scent.handle) +
            '" data-product-title="' + esc(title) + '" aria-pressed="false" data-label-choose="' +
            esc(labels.choose) + '" data-label-comparing="' + esc(labels.comparing) + '">' +
            esc(labels.choose) + '</button>';
    html += '</div>';

    html += '<div class="product-card__info">';
    html += '<h3 class="product-card__title"><a href="' + esc(url) + '">' + esc(title) + '</a></h3>';
    html += '<div class="product-card__price">' + price + '</div>';
    html += '</div>';

    html += '</div>';
    return html;
  }

  /* ----------------------------------------------------------------- init */

  function init(root) {
    if (root.jkScentInit) return;
    root.jkScentInit = true;

    var form = root.querySelector('[data-scent-form]');
    var submit = root.querySelector('[data-scent-submit]');
    var hint = root.querySelector('[data-scent-hint]');
    var result = root.querySelector('[data-scent-result]');
    var resultCard = root.querySelector('[data-scent-result-card]');
    var resultNote = root.querySelector('[data-scent-result-note]');
    var reset = root.querySelector('[data-scent-reset]');
    var options = root.querySelectorAll('[data-scent-option]');

    if (!form || !submit || !result || !resultCard) return;

    var catalog = readCatalog(root);

    var labels = {
      add: attr(root, 'data-label-add'),
      soldOut: attr(root, 'data-label-sold-out'),
      sale: attr(root, 'data-label-sale'),
      view360: attr(root, 'data-label-view-360'),
      choose: attr(root, 'data-label-choose-to-compare'),
      comparing: attr(root, 'data-label-comparing'),
      noMatch: attr(root, 'data-label-no-match'),
      cartAddUrl: attr(root, 'data-cart-add-url') ||
        ((window.routes && window.routes.cart_add_url) || '/cart/add')
    };

    // JSX 1284-1287: four independent pieces of state, all seeded null.
    var answers = { occasion: null, season: null, gender: null, family: null };

    // JSX 1290: family is deliberately absent — it is the optional question.
    function canReveal() {
      return !!(answers.occasion && answers.season && answers.gender);
    }

    function syncOptions() {
      for (var i = 0; i < options.length; i++) {
        var btn = options[i];
        var field = attr(btn, 'data-scent-field');
        var on = answers[field] === attr(btn, 'data-value');
        if (on) {
          btn.classList.add('selected');
        } else {
          btn.classList.remove('selected');
        }
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      }

      var ready = canReveal();
      submit.disabled = !ready;
      // JSX 1362: the hint only exists while the answers are incomplete.
      if (hint) hint.hidden = ready;
    }

    function showResult(scent) {
      if (scent) {
        resultCard.innerHTML = cardMarkup(root, scent, labels);
        if (resultNote) resultNote.textContent = scent.note || '';
      } else {
        resultCard.innerHTML = '';
        if (resultNote) resultNote.textContent = labels.noMatch;
      }

      // JSX 1330/1366: the questions are replaced by the result, not stacked under it.
      form.hidden = true;
      result.hidden = false;
      result.classList.add('in');

      // Let compare.js adopt the freshly injected [data-compare-toggle].
      document.dispatchEvent(new CustomEvent('jk:compare:sync', { bubbles: true }));

      result.setAttribute('tabindex', '-1');
      try { result.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
    }

    function startOver() {
      // JSX 1296-1298
      answers.occasion = null;
      answers.season = null;
      answers.gender = null;
      answers.family = null;

      resultCard.innerHTML = '';
      if (resultNote) resultNote.textContent = '';
      result.hidden = true;
      result.classList.remove('in');
      form.hidden = false;
      syncOptions();
    }

    root.addEventListener('click', function (event) {
      var option = event.target.closest ? event.target.closest('[data-scent-option]') : null;
      if (option && root.contains(option)) {
        var field = attr(option, 'data-scent-field');
        var value = attr(option, 'data-value');
        if (!Object.prototype.hasOwnProperty.call(answers, field)) return;

        // JSX 1352: the family swatch toggles off when re-clicked.
        // JSX 1315/1325/1338: the pills only ever set.
        if (field === 'family') {
          answers.family = answers.family === value ? null : value;
        } else {
          answers[field] = value;
        }
        syncOptions();
        return;
      }

      if (event.target.closest && event.target.closest('[data-scent-submit]')) {
        if (!canReveal()) return; // JSX 1293
        showResult(findScentMatch(catalog, answers));
        return;
      }

      if (reset && event.target.closest && event.target.closest('[data-scent-reset]')) {
        startOver();
      }
    });

    syncOptions();
  }

  function boot() {
    var roots = document.querySelectorAll('[data-scent-finder]');
    for (var i = 0; i < roots.length; i++) {
      try {
        init(roots[i]);
      } catch (e) {
        // A broken finder must never take the rest of the page down.
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme-editor support: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target || !target.querySelectorAll) return;
    var roots = target.querySelectorAll('[data-scent-finder]');
    for (var i = 0; i < roots.length; i++) {
      try { init(roots[i]); } catch (e) { /* noop */ }
    }
  });
})();
